import React, { useCallback, useMemo, useState } from 'react';
import { FieldFilter } from '@components/FieldFilter';
import { FieldFilterBoxSelectExpression } from '@components/filters/FieldFilterBoxSelectExpression';
import { Filter, filterToRawExpression, isTopKFilter, MultiSelectTags, OptionObject, TextInput } from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { ellipsis } from '@fabric-ui/theme';
import { noop } from '@fabric-ui/utils';
import { useAvContext } from '../../context/AvContextProvider';
import TopKDisplay from './components/TopKDisplay';

interface FieldFilterBoxSelectProps {
  fieldName?: string | null;
  size: 'xSmall' | 'small';
  value: Filter;
  onChange: (filter: Filter) => void;
  activeProjName?: string;
  isHistorical?: boolean;
  hideAdvanced?: boolean;
  defaultMode?: 'list' | 'condition';
}

export const FieldFilterBoxSelect = ({
  value,
  onChange,
  fieldName,
  size,
  activeProjName: maybeActiveProjName,
  isHistorical,
  hideAdvanced,
  defaultMode,
}: FieldFilterBoxSelectProps) => {
  const {
    accountEntities: { fieldMap },
  } = useAvContext();

  const activeProjName = useMemo(
    () => maybeActiveProjName || (fieldName ? fieldMap[fieldName]?.mainProjId?.name : undefined),
    [fieldName, fieldMap, maybeActiveProjName]
  );
  const [isOpen, setIsOpen] = useState(false);
  const rawExpression = useMemo(() => filterToRawExpression(value), [value]);

  const filters = useMemo(
    () => value.expression?.stringCondition?.in?.values ?? (rawExpression ? [encodeURIComponent(JSON.stringify(rawExpression))] : []),
    [value, rawExpression]
  );

  const selectedFilterOptions: OptionObject<string>[] = useMemo(() => filters.map(f => ({ label: f || 'Empty', value: f })), [filters]);

  const handleTagDelete = useCallback(
    (index: number) => {
      if (value.expression?.stringCondition?.in?.values) {
        const newFilterList = value.expression.stringCondition.in.values.filter((_, i) => i !== index);
        onChange({
          ...value,
          expression: {
            ...value.expression,
            stringCondition: newFilterList.length > 0 ? { in: { values: newFilterList } } : { contains: '' },
          },
        });
      }
    },
    [filters, fieldName]
  );

  const getTagProps = useCallback(
    ({ index }: { index: number }) => ({
      onDelete: () => handleTagDelete(index),
    }),
    [handleTagDelete]
  );

  const trigger = useMemo(
    () => (
      <TextInput
        sx={{
          width: size === 'xSmall' ? '182px' : '406px',
          whiteSpace: 'nowrap',
          '.MuiOutlinedInput-root': { gap: 0.5, pl: 1, cursor: 'pointer' },
          input: { visibility: 'hidden' },
          '.MuiChip-root': {
            height: size === 'xSmall' ? '18px' : '22px',
            '.MuiChip-label': {
              fontSize: size === 'xSmall' ? '12px' : '14px',
            },
          },
        }}
        /* eslint-disable-next-line react/no-unstable-nested-components */
        startAdornment={() =>
          rawExpression && rawExpression?.and?.[0]?.operator !== 'in' ? (
            rawExpression.type && isTopKFilter(rawExpression.type) ? (
              <TopKDisplay
                metric={rawExpression.value}
                top={rawExpression.top}
                dir={rawExpression.dir}
                style={{ ...ellipsis, width: 'calc(100% - 18px)' }}
              />
            ) : (
              <FieldFilterBoxSelectExpression
                rawExpression={rawExpression}
                key={(rawExpression.and || (rawExpression as any).or)?.length || 0}
              />
            )
          ) : selectedFilterOptions.length > 0 ? (
            <MultiSelectTags
              tags={selectedFilterOptions}
              getTagProps={getTagProps}
              size={size}
              getLabelFunc={({ label }: any) => label}
              getValueFunc={({ value }: any) => value}
            />
          ) : (
            'Select value'
          )
        }
        actionIcons={[<ArrowDown style={isOpen ? { transform: 'rotate(180deg)' } : {}} />]}
      />
    ),
    [size, value, rawExpression, selectedFilterOptions, getTagProps]
  );

  return (
    <FieldFilter
      filters={{ [fieldName!]: filters }}
      disabled={!fieldName}
      field={fieldName ?? ''}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      isEnabled={!!fieldName}
      onChange={noop}
      onFilterChange={onChange}
      size={size}
      activeProjName={activeProjName}
      trigger={trigger}
      disablePortalInOperatorSelect={false}
      isHistorical={isHistorical}
      hideAdvanced={hideAdvanced}
      defaultMode={defaultMode}
    />
  );
};
